import { Button, Checkbox, Col, Modal, Row, Typography } from 'antd';
import { useState, useEffect } from 'react';
import { RxCross2 } from 'react-icons/rx';
import useWindowSize from 'src/hooks/useWindowSize';
import { useAppSelector } from 'src/redux/hook';

type Props = {
  isOpen: boolean;
  handleCancel: () => void;
  onConfirm: (selectedTerms: string[]) => void;
};

type ActiveTerm = {
  name: string;
  description: string; // JSON string with { text: string, autoCheck: boolean }
  value: number;
};

type TermDescription = {
  text: string;
  autoCheck: boolean;
  index?: number;
  ucmCodeMap?: string;
};

const ModalConfirmTerms = (props: Props) => {
  const { isOpen, handleCancel, onConfirm } = props;
  const size = useWindowSize();
  const { masterData } = useAppSelector((state) => state.global);
  const [selectedTerms, setSelectedTerms] = useState<string[]>([]);
  const [showValidation, setShowValidation] = useState(false);

  const activeTerms: ActiveTerm[] = masterData?.activeTerms || [];

  // Parse term description safely
  const parseTermDescription = (description: string): TermDescription => {
    try {
      return JSON.parse(description);
    } catch {
      return { text: description, autoCheck: false };
    }
  };

  // Sort active terms by index field if it exists in description
  const sortedActiveTerms = [...activeTerms].sort((a, b) => {
    const descA = parseTermDescription(a.description);
    const descB = parseTermDescription(b.description);

    // If both have index, sort by index
    if (descA.index !== undefined && descB.index !== undefined) {
      return descA.index - descB.index;
    }
    // If only one has index, prioritize the one with index
    if (descA.index !== undefined) return -1;
    if (descB.index !== undefined) return 1;
    // If neither has index, maintain original order
    return 0;
  });

  useEffect(() => {
    if (isOpen) {
      // Initialize with auto-checked terms
      const autoCheckedTerms = sortedActiveTerms
        .filter((term) => {
          const desc = parseTermDescription(term.description);
          return desc.autoCheck;
        })
        .map((term) => term.name);

      setSelectedTerms(autoCheckedTerms);
      setShowValidation(false);
    }
  }, [isOpen, activeTerms]);

  const handleCheckboxChange = (termName: string, checked: boolean) => {
    if (checked) {
      setSelectedTerms([...selectedTerms, termName]);
    } else {
      setSelectedTerms(selectedTerms.filter((name) => name !== termName));
    }
  };

  const handleSubmit = () => {
    setShowValidation(true);

    // Check if all required terms are selected
    const requiredTerms = sortedActiveTerms.filter((term) => term.value === 1);
    const allRequiredSelected = requiredTerms.every((term) => selectedTerms.includes(term.name));

    if (allRequiredSelected) {
      // Validation passed - proceed
      onConfirm(selectedTerms);
      handleCancel();
    }
    // If validation fails, just show the red backgrounds and don't close modal
  };

  return (
    <Modal
      {...props}
      width={size.width <= 768 ? '95%' : '70%'}
      destroyOnClose
      open={isOpen}
      maskClosable={false}
      className={`custom-modal`}
      onCancel={handleCancel}
      footer={false}
      closable={false}
      centered
    >
      <div className='flex justify-between items-center mb-5'>
        <Typography.Title level={3} className='!my-0'>
          Điều khoản chia sẻ dữ liệu
        </Typography.Title>
        <button
          onClick={handleCancel}
          className='text-3xl cursor-pointer border-transparent bg-transparent'
        >
          <RxCross2 />
        </button>
      </div>

      <div className='mb-6'>
        <Row className='bg-[#F7F7F8] p-3 rounded font-semibold' gutter={16}>
          <Col span={2} className='px-3'>
            STT
          </Col>
          <Col span={19}>MÔ TẢ ĐIỀU KHOẢN</Col>
          <Col span={3} className='text-center'>
            CHỌN
          </Col>
        </Row>

        <div className='mt-3'>
          {sortedActiveTerms.map((term, index) => {
            const termDesc = parseTermDescription(term.description);
            const isRequired = term.value === 1;
            const isChecked = selectedTerms.includes(term.name);
            const showError = showValidation && isRequired && !isChecked;

            return (
              <Row
                key={term.name}
                className={`p-3 border-b border-[#F0F0F0] items-start transition-colors cursor-pointer rounded ${
                  showError ? 'bg-[#ffebee] hover:bg-[#ffcdd2]' : 'hover:bg-[#e6f4ff]'
                }`}
                gutter={16}
              >
                <Col span={2} className='pt-1 px-3 text-black'>
                  {String(index + 1).padStart(2, '0')}
                </Col>
                <Col span={19}>
                  <div className='text-black' dangerouslySetInnerHTML={{ __html: termDesc.text }} />
                </Col>
                <Col span={3} className='flex justify-center pt-1'>
                  <Checkbox
                    checked={isChecked}
                    onChange={(e) => handleCheckboxChange(term.name, e.target.checked)}
                  />
                </Col>
              </Row>
            );
          })}
        </div>
      </div>

      <div className='flex justify-end gap-3'>
        <Button onClick={handleCancel}>Hủy</Button>
        <Button type='primary' onClick={handleSubmit}>
          Lưu
        </Button>
      </div>
    </Modal>
  );
};
export default ModalConfirmTerms;
